import React, { useState, useEffect, useRef } from 'react'
import './Manufacturing.css'
import TeamHeader from '../TeamHeader/TeamHeader'
import { useDetectorRelations } from '../HardwareTab/useDetectorRelations';
import FilterDropdown from '../TeamPortalUtil/FilterDropdown';

// Chart imports
import Chart from "react-apexcharts";

// Leaflet imports
import {
    MapContainer,
    TileLayer,
    ScaleControl,
    Marker,
    Popup
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { Icon, Map as LeafletMap, LeafletEvent } from "leaflet";

// Toast import 
import { toast, ToastContainer } from 'react-toastify';

// Icons
import { FaTrashAlt } from "react-icons/fa";
import { FaFileCsv } from "react-icons/fa6";
import { IoMdNotificationsOutline } from "react-icons/io";
import { BiSolidArchive } from "react-icons/bi";
import { MdAddLocationAlt, MdOutlineInventory } from "react-icons/md";

// Assets
import plannedMarker from './assets/planned-loc-icon.png'
import approvedPlannedMarker from './assets/approved-planned-loc-icon.png'
import blueMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-blue.png'
import offlineMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-grey.png'
import googleMapsLogo from '../../backend/assets/google-map-logo.png'

// Detector version
interface Detector {
    id: number;
    name: string;
    version: string;
}

interface Batch {
    batchID: number;
    batch_name: string;
    region: string;
    detector_version: number;
    target_quantity: number;
    shipment_due: string;
    status: string;
    planning_started_at: string;
    notes: string;
    detectors_built: number;
    subassemblies_built: number;
    is_complete: boolean;
}

interface BatchProgress {
    batchID: number;
    batch_name: string;
    status: string;
    time: {
        planning_days: number;
        in_progress_days: number;
    };
    detectors: { target: number; built: number; remaining: number, deployed: number };
    subassemblies: {
        total_needed: number;
        built: number;
        bom_per_detector: Record<string, number>;
        built_breakdown: Record<string, number>;
    };
}

interface PlannedDetectorLocation {
    loc_id: number;
    location_name: string;
    region?: string | null;
    address?: string | null;
    county?: string | null;
    latitude: number;
    longitude: number;
    planned_deployment_date?: string | null;
    contact_person?: string | null;
    contact_email?: string | null;
    contact_number?: string | null;
    note?: string | null;
    is_authorized: boolean;
    is_deployed: boolean;
}


export const plannedIcon = new Icon({
    iconUrl: plannedMarker,
    iconSize: [50, 50],

    // Bottom center of icon
    iconAnchor: [25, 50],

    // Popup opens above marker tip
    popupAnchor: [0, -45],
});

export const approvedPlannedIcon = new Icon({
    iconUrl: approvedPlannedMarker,
    iconSize: [50, 50],

    // Bottom center of icon
    iconAnchor: [25, 50],

    // Popup opens above marker tip
    popupAnchor: [0, -45],
});

export const BwIcon = new Icon({
    iconUrl: offlineMarker,
    iconSize: [50, 50],

    iconAnchor: [25, 50],
    popupAnchor: [0, -45],
});

export const blueIcon = new Icon({
    iconUrl: blueMarker,
    iconSize: [50, 50],

    iconAnchor: [25, 50],
    popupAnchor: [0, -45],
});

const Manufacturing = () => {

    // Map Calls
    const { fullDetectors, plannedLocations, refreshDetectors } = useDetectorRelations();

    const [mapFilter, setMapFilter] = useState<string[]>(['Online', 'Offline', 'Planned']);

    const filteredDetectors = fullDetectors?.filter(det => {
        const isOnline = det.monitoring.online;
        return isOnline ? mapFilter.includes('Online') : mapFilter.includes('Offline');
    });

    const filteredPlannedLocations = mapFilter.includes('Planned') ? plannedLocations : [];

    // Utils
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to the top when the component mounts
    }, []);


    const formatDate = (dateString?: string, timeOnSecondLine = true) => {
        if (!dateString) return "N/A";

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "N/A";

        // Detect if time exists in the original string
        const hasTime =
            dateString.includes("T") || // ISO format
            dateString.includes(":");   // fallback (just in case)

        // Format date in UTC
        const formattedDate = date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            timeZone: "UTC",
        });

        // If NO time → return only date
        if (!hasTime) {
            return <span>{formattedDate}</span>;
        }

        // Format time in UTC with AM/PM
        const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "UTC",
        });

        if (timeOnSecondLine) {
            return (
                <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2" }}>
                    <span>{formattedDate}</span>
                    <span>{formattedTime}</span>
                </div>
            );
        } else {
            return (
                <span>
                    {formattedDate}, {formattedTime}
                </span>
            );
        }
    };

    // Status
    const BatchStatuses = [
        { value: 'planning', label: 'Planning', color: '#1976D2' },
        { value: 'in_progress', label: 'In Progress', color: '#f9a825' },
        { value: 'completed', label: 'Completed', color: '#20bf55' },
        { value: 'cancelled', label: 'Cancelled', color: '#d32f2f' },
    ];

    const batchStatusMap = Object.fromEntries(
        BatchStatuses.map(s => [s.value, s])
    );

    // Variables
    const [batches, setBatches] = useState<Batch[]>([]);
    const [selectedBatchProgress, setSelectedBatchProgress] = useState<BatchProgress | null>(null);
    const [batchProgressMap, setBatchProgressMap] = useState<Record<number, BatchProgress>>({});
    const [detectors, setDetectors] = useState<Detector[]>([]);
    const [editingBatch, setEditingBatch] = useState<Batch | null>(null);
    const [batchToDelete, setBatchToDelete] = useState<Batch | null>(null);
    const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
    const [hoveredSubAssembly, setHoveredSubAssembly] = useState<{ name: string; built: number; needed: number } | null>(null);
    const [isDetectorHovered, setIsDetectorHovered] = useState(false);

    // Form fields
    const [batchName, setBatchName] = useState('');
    const [batchRegion, setBatchRegion] = useState('');
    const [batchDetectorVersion, setBatchDetectorVersion] = useState<number | ''>('');
    const [batchTargetQty, setBatchTargetQty] = useState<number | ''>('');
    const [batchShipmentDue, setBatchShipmentDue] = useState('');
    const [batchNotes, setBatchNotes] = useState('');

    // Popup Variables
    const [showBatchPopup, setShowBatchPopup] = useState(false);
    const [showConfirmDeleteBatchPopup, setShowConfirmDeleteBatchPopup] = useState(false);
    const [showPlannedLocationsPopup, setShowPlannedLocationsPopup] = useState(false);
    const [showAddorEditPlannedLocationsPopup, setShowAddorEditPlannedLocationsPopup] = useState(false);

    // CSRF
    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    // Performing fetch for the detector versions
    const fetchDetectors = async () => {
        try {
            const response = await fetch('/api/inventory_detectors/', {
                credentials: "same-origin"
            });
            const data = await response.json();
            if (response.ok) {
                setDetectors(data);
            } else {
                console.error("Failed to load detectors:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching detectors:", error);
        }
    };

    useEffect(() => {
        fetchDetectors();
    }, []);

    // Performing initial fetch for all batces
    const fetchBatches = async () => {
        try {
            const response = await fetch('/api/inventory_batches/', {
                credentials: "same-origin"
            });

            const data = await response.json();

            if (response.ok) {
                setBatches(data);
            } else {
                console.error("Failed to load batch information:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching batch info:", error);
        }
    };

    useEffect(() => {
        fetchBatches();
    }, []);

    const openCreateBatch = () => {
        setEditingBatch(null);
        setBatchName('');
        setBatchRegion('');
        setBatchDetectorVersion('');
        setBatchTargetQty('');
        setBatchShipmentDue('');
        setBatchNotes('');
        setShowBatchPopup(true);
    };

    const openEditBatch = (batch: Batch) => {
        setEditingBatch(batch);
        setBatchName(batch.batch_name);
        setBatchRegion(batch.region);
        setBatchDetectorVersion(batch.detector_version);
        setBatchTargetQty(batch.target_quantity);
        setBatchShipmentDue(batch.shipment_due || '');
        setBatchNotes(batch.notes || '');
        setShowBatchPopup(true);
    };

    const saveBatch = async () => {

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const payload = {
            batch_name: batchName,
            region: batchRegion,
            detector_version:
                editingBatch && ["in_progress", "completed"].includes(editingBatch.status)
                    ? editingBatch.detector_version
                    : batchDetectorVersion,
            target_quantity: batchTargetQty,
            shipment_due: batchShipmentDue || null,
            notes: batchNotes,
        };

        try {
            const url = editingBatch
                ? `/api/inventory_batches/${editingBatch.batchID}/`
                : `/api/inventory_batches/`;

            const method = editingBatch ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                credentials: "same-origin",
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                toast.success(editingBatch ? "Batch updated" : "Batch created");
                setShowBatchPopup(false);
                fetchBatches();
            } else {
                const data = await res.json();
                toast.error(data.error || "Failed to save batch");
            }
        } catch (err) {
            toast.error("Error saving batch");
        }
    };

    const handleDeleteBatch = async () => {
        if (!batchToDelete) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const res = await fetch(
                `/api/inventory_batches/${batchToDelete.batchID}/`,
                {
                    method: "DELETE",
                    credentials: "same-origin",
                    headers: {
                        'X-CSRFToken': csrfToken,
                    },
                }
            );

            if (res.ok) {
                toast.success("Batch deleted");
                fetchBatches();
            } else {
                toast.error("Failed to delete batch");
            }
        } catch {
            toast.error("Error deleting batch");
        }
    };

    // Batch Progress Logic
    const fetchBatchProgress = async (batchId: number) => {
        const response = await fetch(
            `/api/inventory_batches/${batchId}/progress/`,
            { credentials: "same-origin" }
        );

        if (response.ok) {
            const data = await response.json();

            setBatchProgressMap(prev => ({
                ...prev,
                [batchId]: data
            }));

            setSelectedBatchProgress(data);
        } else {
            setSelectedBatchProgress(null);
        }
    };

    useEffect(() => {
        batches.forEach(b => {
            fetchBatchProgress(b.batchID);
        });
    }, [batches]);

    useEffect(() => {
        if (batches.length > 0 && selectedBatchId === null) {
            const active = batches.find(b => b.status !== 'completed' && b.status !== 'cancelled');
            if (active) {
                setSelectedBatchId(active.batchID);
                fetchBatchProgress(active.batchID);
            }
        }
    }, [batches]);

    const [avgStats, setAvgStats] = useState({
        planning: 0,
        inProgress: 0,
        completion: 0
    });

    const fetchAverageStats = async () => {
        try {
            const results = await Promise.all(
                batches.map(async (b) => {
                    try {
                        const res = await fetch(
                            `/api/inventory_batches/${b.batchID}/progress/`,
                            { credentials: "same-origin" }
                        );
                        if (!res.ok) return null;
                        return await res.json();
                    } catch {
                        return null;
                    }
                })
            );

            const valid = results.filter(Boolean);

            let planningSum = 0;
            let inProgressSum = 0;
            let completionSum = 0;

            let totalWeight = 0;

            valid.forEach((p: BatchProgress) => {
                const weight = p.detectors.target || 0; // 👈 batch size

                const planning = p.time.planning_days || 0;
                const inProgress = p.time.in_progress_days || 0;

                if (weight > 0) {
                    planningSum += planning * weight;
                    inProgressSum += inProgress * weight;
                    completionSum += (planning + inProgress) * weight;

                    totalWeight += weight;
                }
            });

            setAvgStats({
                planning: totalWeight ? planningSum / totalWeight : 0,
                inProgress: totalWeight ? inProgressSum / totalWeight : 0,
                completion: totalWeight ? completionSum / totalWeight : 0
            });

        } catch (err) {
            console.error("Failed to compute averages", err);
        }
    };

    useEffect(() => {
        if (batches.length > 0) {
            fetchAverageStats();
        }
    }, [batches]);

    const formatAvgDays = (val: number) => {
        if (!val) return "-";
        return `${Math.max(1, Number(val.toFixed(2)))} Days`;
    };

    // ---------------------------------------------------------
    // Alerts Logic
    // ---------------------------------------------------------

    const [manufacturingAlerts, setManufacturingAlerts] = useState<any[]>([]);
    const [showAlertsDropdown, setShowAlertsDropdown] = useState(false);
    const alertsDropdownRef = useRef<HTMLDivElement>(null);

    const fetchManufacturingAlerts = async () => {
        try {
            const res = await fetch(
                `/api/alerts/?department=Manufacturing&archive=false&page=1&page_size=50`,
                { credentials: "same-origin" }
            );
            if (!res.ok) return;
            const data = await res.json();
            setManufacturingAlerts(data.results ?? data);
        } catch (err) {
            console.error("Failed to fetch manufacturing alerts:", err);
        }
    };

    const toggleArchiveAlert = async (alertId: number, currentlyArchived: boolean) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const res = await fetch(`/api/alerts/${alertId}/archive`, {
                method: "PUT",
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                body: JSON.stringify({ archive: !currentlyArchived })
            });

            if (!res.ok) throw new Error("Failed to update alert");

            // Optimistically update local state
            setManufacturingAlerts(prev =>
                prev.filter(a => a.id !== alertId)
            );
        } catch (err) {
            console.error(err);
        }
    };

    const archiveAllAlerts = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            await Promise.all(
                manufacturingAlerts.map(alert =>
                    fetch(`/api/alerts/${alert.id}/archive`, {
                        method: "PUT",
                        credentials: "same-origin",
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRFToken': csrfToken
                        },
                        body: JSON.stringify({ archive: true })
                    })
                )
            );
            setManufacturingAlerts([]);
        } catch (err) {
            console.error("Failed to archive all alerts:", err);
        }
    };

    useEffect(() => {
        fetchManufacturingAlerts();
        const interval = setInterval(fetchManufacturingAlerts, 15000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (alertsDropdownRef.current && !alertsDropdownRef.current.contains(e.target as Node)) {
                setShowAlertsDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // ---------------------------------------------------------
    // Planned locations Logic
    // ---------------------------------------------------------
    const [plannedLocSearch, setPlannedLocSearch] = useState('');

    const [editingLocation, setEditingLocation] = useState<PlannedDetectorLocation | null>(null);

    // Location form fields
    const [locName, setLocName] = useState('');
    const [locRegion, setLocRegion] = useState('');
    const [locAddress, setLocAddress] = useState('');
    const [locCounty, setLocCounty] = useState('');
    const [locLat, setLocLat] = useState<number | ''>('');
    const [locLon, setLocLon] = useState<number | ''>('');
    const [locPlannedDate, setLocPlannedDate] = useState('');
    const [locContactPerson, setLocContactPerson] = useState('');
    const [locContactEmail, setLocContactEmail] = useState('');
    const [locContactNumber, setLocContactNumber] = useState('');
    const [locNote, setLocNote] = useState('');
    const [locIsDeployed, setLocIsDeployed] = useState(false);
    const [locIsAuthorized, setLocIsAuthorized] = useState(false);
    const [showConfirmDeleteLocPopup, setShowConfirmDeleteLocPopup] = useState(false);
    const [locToDelete, setLocToDelete] = useState<PlannedDetectorLocation | null>(null);
    const [selectedLocations, setSelectedLocations] = useState<Set<number>>(new Set());
    const [isAddressManuallyEdited, setIsAddressManuallyEdited] = useState(false);

    const [plannedLocationFilters, setPlannedLocationFilters] = useState<{
        region: string[];
        status: string[];
    }>({
        region: [],
        status: [],
    });

    const plannedRegions: string[] = Array.from(
        new Set(
            (plannedLocations ?? [])
                .map(l => l.region)
                .filter((r): r is string => typeof r === "string" && r.trim() !== "")
        )
    );

    const reverseGeocode = async (lat: number, lon: number) => {
        if (!lat || !lon) return;

        try {
            const res = await fetch(
                `/portal/api/loqiq-reverse-geocode/?lat=${lat}&lon=${lon}`,
                { credentials: "same-origin" }
            );

            const data = await res.json();

            if (res.ok) {

                // Only overwrite if user has NOT manually edited it
                if (!isAddressManuallyEdited) {
                    setLocAddress(data.display_name || "");
                }

                setLocRegion(data.region);
                setLocCounty(data.county);

            } else {
                toast.error(data.error || "Reverse geocoding failed");
            }
        } catch {
            toast.error("Reverse geocoding failed");
        }
    };

    const openCreateLocation = () => {
        setEditingLocation(null);

        setIsAddressManuallyEdited(false);

        setLocName('');
        setLocRegion('');
        setLocAddress('');
        setLocCounty('');
        setLocLat('');
        setLocLon('');
        setLocPlannedDate('');
        setLocContactPerson('');
        setLocContactEmail('');
        setLocContactNumber('');
        setLocNote('');
        setLocIsDeployed(false);
        setLocIsAuthorized(false);

        setShowAddorEditPlannedLocationsPopup(true);
    };

    const openEditLocation = (loc: PlannedDetectorLocation) => {
        setEditingLocation(loc);

        setIsAddressManuallyEdited(false);

        setLocName(loc.location_name);
        setLocRegion(loc.region || '');
        setLocAddress(loc.address || '');
        setLocCounty(loc.county || '');
        setLocLat(loc.latitude);
        setLocLon(loc.longitude);
        setLocPlannedDate(loc.planned_deployment_date || '');
        setLocContactPerson(loc.contact_person || '');
        setLocContactEmail(loc.contact_email || '');
        setLocContactNumber(loc.contact_number || '');
        setLocNote(loc.note || '');
        setLocIsDeployed(loc.is_deployed);
        setLocIsAuthorized(loc.is_authorized);

        setShowAddorEditPlannedLocationsPopup(true);
    };

    const saveLocation = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const payload = {
            location_name: locName,
            region: locRegion || null,
            address: locAddress || null,
            county: locCounty || null,
            latitude: locLat,
            longitude: locLon,
            planned_deployment_date: locPlannedDate || null,
            contact_person: locContactPerson || null,
            contact_email: locContactEmail || null,
            contact_number: locContactNumber || null,
            note: locNote || null,
            is_deployed: editingLocation ? locIsDeployed : false,
            is_authorized: locIsAuthorized || false,
        };

        try {
            const url = editingLocation
                ? `/api/detector_planned_locations/${editingLocation.loc_id}/`
                : `/api/detector_planned_locations/`;
            const method = editingLocation ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
                credentials: "same-origin",
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                toast.success(editingLocation ? "Location updated" : "Location created");
                setShowAddorEditPlannedLocationsPopup(false);
                refreshDetectors();
            } else {
                const data = await res.json();
                toast.error(data.error || "Failed to save location");
            }
        } catch {
            toast.error("Error saving location");
        }
    };

    const handleDeleteLocation = async () => {
        if (!locToDelete) return;
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const res = await fetch(
                `/api/detector_planned_locations/${locToDelete.loc_id}/`,
                { method: "DELETE", credentials: "same-origin", headers: { 'X-CSRFToken': csrfToken } }
            );

            if (res.ok) {
                toast.success("Location deleted");
                setShowConfirmDeleteLocPopup(false);
                setShowAddorEditPlannedLocationsPopup(false);
                refreshDetectors();
            } else {
                toast.error("Failed to delete location");
            }
        } catch {
            toast.error("Error deleting location");
        }
    };

    // Filtering the selected locations in order to export them:
    const filteredPlannedLocationsForTable = (plannedLocations ?? []).filter(loc => {
        const q = plannedLocSearch.toLowerCase();

        const matchesSearch =
            loc.location_name?.toLowerCase().includes(q) ||
            loc.address?.toLowerCase().includes(q) ||
            loc.contact_person?.toLowerCase().includes(q) ||
            loc.contact_email?.toLowerCase().includes(q);

        const matchesRegion =
            plannedLocationFilters.region.length === 0 ||
            plannedLocationFilters.region.includes(loc.region || '');

        const matchesStatus =
            plannedLocationFilters.status.length === 0 ||
            plannedLocationFilters.status.includes(
                loc.is_deployed ? 'Deployed' : 'Planned'
            );

        return matchesSearch && matchesRegion && matchesStatus;
    });

    const toggleLocationSelection = (locId: number) => {
        setSelectedLocations(prev => {
            const newSet = new Set(prev);
            if (newSet.has(locId)) newSet.delete(locId);
            else newSet.add(locId);
            return newSet;
        });
    };

    const exportSelectedLocations = () => {

        const selected = filteredPlannedLocationsForTable.filter(loc => selectedLocations.has(loc.loc_id));

        if (selected.length === 0) {
            toast.warning('No locations selected');
            return;
        }

        const headers = [
            'Location Name',
            'Latitude',
            'Longitude',
            'Google Maps Link',
            'Address - Approximated',
            'Region',
            'County',
            'Planned Date',
            'Contact Person',
            'Contact Email',
            'Contact Number',
            'Installation Authorized',
            'Status',
            'Notes'
        ];

        const rows = selected.map(loc => [
            loc.location_name,
            loc.latitude,
            loc.longitude,
            `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`,
            loc.address || '',
            loc.region || '',
            loc.county || '',
            loc.planned_deployment_date || '',
            loc.contact_person || '',
            loc.contact_email || '',
            loc.contact_number || '',
            loc.is_authorized ? 'Yes' : 'No',
            loc.is_deployed ? 'Deployed' : 'Planned',
            loc.note || '',
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(r => r.map(v => `"${v}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute(
            'download',
            `planned_locations_${new Date().toISOString().split('T')[0]}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success(`${selected.length} location${selected.length !== 1 ? 's' : ''} exported`);
    };


    return (
        <div className="customer-directory-body toa-body">
            <TeamHeader title="Manufacturing" />

            <ToastContainer
                style={{ zIndex: 99999 }}
            />

            <div className="customer-title-and-expand-buttons">
                <h1 className="cutomer-title">Manufacturing & Deployment</h1>

                <div className='manu-alert-notification' ref={alertsDropdownRef} style={{ position: 'relative' }}>
                    <button
                        className="manu-alerts-btn"
                        onClick={() => setShowAlertsDropdown(prev => !prev)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                        <IoMdNotificationsOutline />
                        <div className="alerts-text-wrapper">
                            <p>Alerts</p>
                            {manufacturingAlerts.length > 0 && (
                                <span className="alerts-badge">{manufacturingAlerts.length}</span>
                            )}
                        </div>
                    </button>

                    {showAlertsDropdown && (
                        <div className="manu-alerts-dropdown">
                            <div className="manu-alerts-dropdown-header">
                                <span>Alerts</span>
                                <button className='archive-button' onClick={archiveAllAlerts}>
                                    <BiSolidArchive /> All
                                </button>
                            </div>

                            {manufacturingAlerts.length === 0 ? (
                                <p className="manu-alerts-empty">No active alerts</p>
                            ) : (
                                <div className="manu-alerts-list">
                                    {manufacturingAlerts.map(alert => (
                                        <div
                                            key={alert.id}
                                            className={`manu-alert-item manu-alert-${alert.type?.toLowerCase()}`}
                                            onClick={() => {
                                                window.open(
                                                    `/alert_hub?department=Manufacturing&search=${encodeURIComponent(alert.title)}`,
                                                    '_blank'
                                                );
                                                setShowAlertsDropdown(false);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="manu-alert-item-header">
                                                <span className="manu-alert-type-badge">{alert.type}</span>
                                                <span className="manu-alert-date">
                                                    {new Date(alert.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <p className="manu-alert-title">{alert.title}</p>
                                            <p className="manu-alert-body">{alert.body}</p>

                                            <button
                                                className='archive-button'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleArchiveAlert(alert.id, alert.archive);
                                                }}
                                            >
                                                <BiSolidArchive /> Archive
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className='manu-alerts-dropdown-footer'>
                                <button onClick={() => {
                                    window.open('/alert_hub?department=Manufacturing', '_blank');
                                    setShowAlertsDropdown(false);
                                }}>
                                    View All
                                </button>
                            </div>
                        </div>
                    )}
                </div>

            </div>

            <div className='toa-dash-container'>

                {/* Map Block */}
                <div className="toa-dash-block">
                    <div className="toa-map-wrapper" style={{ position: 'relative' }}>

                        <div className='gis-in-map-buttons'>
                            <FilterDropdown
                                sideClass="detector-side-drop"
                                initialSelected={{
                                    visibility: ['Online', 'Offline', 'Planned'],
                                }}
                                sections={[
                                    {
                                        key: 'visibility',
                                        title: 'Show',
                                        options: ['Online', 'Offline', 'Planned'],
                                        type: 'checkbox',
                                    },
                                ]}
                                onChange={(filters) => {
                                    setMapFilter(filters.visibility ?? []);
                                }}
                            />

                            <button
                                className="general-button batch-button2"
                                onClick={() => setShowPlannedLocationsPopup(true)}
                            >
                                Planned Locations
                            </button>

                            <MdAddLocationAlt className='add-location-icon' onClick={openCreateLocation} />
                        </div>

                        <MapContainer
                            center={[0, 0]}
                            zoom={0}
                            minZoom={2}
                            style={{ width: "100%", height: "100%" }}
                        >
                            <TileLayer
                                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            />

                            {/* Planned Locations */}
                            {filteredPlannedLocations
                                ?.filter(loc => !loc.is_deployed) // hide deployed ones
                                .map((loc) => (
                                    <Marker
                                        key={`planned-${loc.loc_id}`}
                                        position={[loc.latitude, loc.longitude]}
                                        icon={loc.is_authorized ? approvedPlannedIcon : plannedIcon}
                                    >
                                        <Popup className="det-popup-wrapper" minWidth={260}>
                                            <div className="det-popup" style={{ cursor: 'pointer' }} onClick={() => openEditLocation(loc)}>
                                                <div className="det-popup-header">
                                                    <span className="det-popup-dot planned" />
                                                    <span className="det-popup-name">{loc.location_name}</span>
                                                </div>
                                                {loc.address && (
                                                    <div className="det-popup-row">
                                                        <span className="det-popup-label">Address</span>
                                                        <span className="det-popup-value">{loc.address}</span>
                                                    </div>
                                                )}
                                                <div className="det-popup-row">
                                                    <span className="det-popup-label">Status</span>
                                                    <span className={`det-popup-badge ${loc.is_deployed ? "deployed" : "planned"}`}>
                                                        {loc.is_deployed ? "Deployed" : "Planned"}
                                                    </span>
                                                </div>
                                                {loc.planned_deployment_date && (
                                                    <div className="det-popup-row">
                                                        <span className="det-popup-label">Planned Date</span>
                                                        <span className="det-popup-value">
                                                            {formatDate(loc.planned_deployment_date, false)}
                                                        </span>
                                                    </div>
                                                )}
                                                {loc.contact_person && (
                                                    <div className="det-popup-row">
                                                        <span className="det-popup-label">Contact</span>
                                                        <span className="det-popup-value">{loc.contact_person}</span>
                                                    </div>
                                                )}
                                                {loc.contact_email && (
                                                    <div className="det-popup-row">
                                                        <span className="det-popup-label">Email</span>
                                                        <span className="det-popup-value">{loc.contact_email}</span>
                                                    </div>
                                                )}
                                                {loc.contact_number && (
                                                    <div className="det-popup-row">
                                                        <span className="det-popup-label">Phone</span>
                                                        <span className="det-popup-value">{loc.contact_number}</span>
                                                    </div>
                                                )}

                                                <div className="det-popup-row">
                                                    <span className="det-popup-label">Authorized</span>
                                                    <span
                                                        className="det-popup-value"
                                                        style={{
                                                            color: loc.is_authorized ? "#20bf55" : "#d32f2f",
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {loc.is_authorized ? "Yes" : "No"}
                                                    </span>
                                                </div>

                                                {loc.note && (
                                                    <div className="det-popup-note">
                                                        <strong>Notes:</strong> {loc.note}
                                                    </div>
                                                )}

                                                <div className="det-popup-row">
                                                    <div
                                                        className='google-maps-button-div google-maps-button-manu'
                                                        onClick={() =>
                                                            window.open(
                                                                `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`,
                                                                "_blank",
                                                                "noopener,noreferrer"
                                                            )
                                                        }
                                                    >
                                                        <img src={googleMapsLogo} alt='gmaps' />
                                                        <p>Google Maps</p>
                                                    </div>
                                                </div>

                                            </div>

                                        </Popup>
                                    </Marker>
                                ))}

                            {/* Live Detectors */}
                            {filteredDetectors?.map((det, i) => {
                                const lat = det.monitoring?.status?.gps_lat;
                                const lon = det.monitoring?.status?.gps_long;
                                if (!lat || !lon) return null;
                                const isOnline = det.monitoring.online;

                                return (
                                    <Marker
                                        key={`live-${det.monitoring.mac}-${i}`}
                                        position={[lat, lon]}
                                        icon={isOnline ? blueIcon : BwIcon}
                                    >
                                        <Popup className="det-popup-wrapper2" minWidth={260}>
                                            <div className="det-popup2">
                                                <div className="det-popup-header">
                                                    <span className={`det-popup-dot ${isOnline ? "online" : "offline"}`} />
                                                    <span className="det-popup-name">
                                                        {det.code?.name || "Detector"}
                                                    </span>
                                                </div>
                                                <div className="det-popup-row">
                                                    <span className="det-popup-label">MAC</span>
                                                    <span className="det-popup-value">{det.monitoring.mac}</span>
                                                </div>
                                                <div className="det-popup-row">
                                                    <span className="det-popup-label">Status</span>
                                                    <span className={`det-popup-badge ${isOnline ? "online" : "offline"}`}>
                                                        {isOnline ? "Online" : "Offline"}
                                                    </span>
                                                </div>
                                                <div className="det-popup-row">
                                                    <span className="det-popup-label">GPS Fix</span>
                                                    <span className="det-popup-value"

                                                        style={{
                                                            color: det.monitoring.status.gps_fix ? "#20bf55" : "#d32f2f",
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {det.monitoring.status.gps_fix ? "Yes" : "No"}
                                                    </span>
                                                </div>
                                                <div
                                                    className='google-maps-button-div google-maps-button-manu'
                                                    onClick={() =>
                                                        window.open(
                                                            `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`,
                                                            "_blank",
                                                            "noopener,noreferrer"
                                                        )
                                                    }
                                                >
                                                    <img src={googleMapsLogo} alt='gmaps' />
                                                    <p>Google Maps</p>
                                                </div>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}

                            <ScaleControl
                                position="bottomleft"
                                metric={true}
                                imperial={false}
                                maxWidth={200}
                            />
                        </MapContainer>
                    </div>
                </div>

                { /* Batch Management Block */}
                <div className="toa-dash-block">

                    {/* Title and Button */}
                    <div className="request-title-and-times toa-location">
                        <h1 className='toa-dash-block-performance-title'>Production Management</h1>

                        <button className="general-button batch-button" onClick={openCreateBatch}>
                            + Add New Batch
                        </button>

                    </div>

                    { /* Table */}
                    <div className="mailjet-contact-table">
                        <div className="mailjet-contact-table-header batch-table-header">
                            <div>Batch</div>
                            <div>Region</div>
                            <div>Detectors</div>
                            <div>Shipment Due</div>
                            <div>Status</div>
                        </div>

                        <div className="mailjet-contact-table-body">

                            {batches.map((batch) => {
                                const statusMeta = batchStatusMap[batch.status];

                                return (
                                    <div
                                        className="mailjet-contact-table-row batch-table-row"
                                        style={{ cursor: "pointer" }}
                                        key={batch.batchID}
                                        onClick={() => openEditBatch(batch)}
                                    >
                                        <div className="mailjet-contact-code">{batch.batch_name}</div>
                                        <div className="mailjet-contact-code">{batch.region}</div>
                                        <div className="mailjet-contact-code">{batch.target_quantity}</div>
                                        <div className="mailjet-contact-code">{formatDate(batch.shipment_due)}</div>

                                        <div
                                            className="mailjet-contact-code"
                                            style={{
                                                fontWeight: 600,
                                                color: statusMeta?.color ?? "inherit",
                                            }}
                                        >
                                            {statusMeta?.label ?? batch.status}
                                        </div>
                                    </div>
                                );
                            })}


                        </div>



                    </div>


                </div>

                { /* Progress Block */}
                <div className="toa-dash-block full-width-section">
                    {/* Title and Button */}
                    <div className="request-title-and-times toa-location">

                        <div style={{ display: "flex", alignItems: 'center', gap: "0.5rem" }}>
                            <h1 className='toa-dash-block-performance-title'>Production Progress</h1>
                            <button
                                className="det-version-button"
                                title='Click to check orders associated with this batch'
                                onClick={() => {
                                    const batchId = selectedBatchId;

                                    window.open(
                                        `/inventory?tab=orders&batch=${batchId}`,
                                        "_blank"
                                    );
                                }}
                            >
                                <strong><MdOutlineInventory /></strong>
                            </button>
                        </div>

                        {/* Select for which batch */}
                        <select
                            value={selectedBatchId ?? ''}
                            onChange={(e) => {
                                const id = Number(e.target.value);
                                setSelectedBatchId(id);
                                fetchBatchProgress(id);
                            }}
                            className='batch-select'
                        >
                            {batches
                                .filter(b => b.status !== 'cancelled')
                                .map(b => {
                                    const progress = batchProgressMap[b.batchID];

                                    const assembled = progress?.detectors.built ?? b.detectors_built;
                                    const deployed = progress?.detectors.deployed ?? 0;

                                    return (
                                        <option key={b.batchID} value={b.batchID}>
                                            {b.batch_name}
                                            {` - ${assembled} assembled | ${deployed} deployed`}
                                        </option>
                                    );
                                })}
                        </select>

                    </div>


                    <div className="toa-charts">
                        {/* Charts */}
                        <div className="toa-chart toa-chart-half">
                            <div className='batch-donuts-wrapper'>

                                {/* Sub-assemblies Radial Bar */}
                                <div className='batch-donut'>
                                    {selectedBatchProgress && (() => {
                                        const sub = selectedBatchProgress.subassemblies;
                                        const detectorTarget = selectedBatchProgress.detectors.target;
                                        const labels = Object.keys(sub.bom_per_detector);
                                        const series = labels.map(name => {
                                            const needed = (sub.bom_per_detector[name] || 0) * detectorTarget;
                                            const built = sub.built_breakdown[name] || 0;
                                            return needed > 0 ? Math.min(100, Math.round((built / needed) * 100)) : 0;
                                        });
                                        const overallPct = sub.total_needed > 0
                                            ? Math.min(100, Math.round((sub.built / sub.total_needed) * 100))
                                            : 0;

                                        const COLORS = ['#28aee1', '#1D9E75', '#D85A30', '#7F77DD', '#BA7517'];

                                        const chartOptions = {
                                            chart: {
                                                type: 'radialBar' as const,
                                                background: 'transparent',
                                                sparkline: { enabled: true },
                                                events: {
                                                    dataPointMouseEnter: (_: any, __: any, config: any) => {
                                                        const name = labels[config.dataPointIndex];
                                                        const needed = (sub.bom_per_detector[name] || 0) * detectorTarget;
                                                        const built = sub.built_breakdown[name] || 0;
                                                        setHoveredSubAssembly({ name, built, needed });
                                                    },
                                                    dataPointMouseLeave: () => setHoveredSubAssembly(null),
                                                }
                                            },
                                            plotOptions: {
                                                radialBar: {
                                                    hollow: { size: '34%' },
                                                    track: { margin: 2 },
                                                    dataLabels: { show: false }
                                                }
                                            },
                                            colors: COLORS,
                                            labels,
                                            tooltip: { enabled: false }
                                        };

                                        return (
                                            <div style={{ width: '100%', display: "flex", alignItems: 'center' }}>
                                                <div style={{ position: 'relative', flexShrink: 0, width: '50%' }}>
                                                    <Chart options={chartOptions} series={series} type="radialBar" height={200} />
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        textAlign: 'center',
                                                        pointerEvents: 'none',
                                                        lineHeight: '1.3',
                                                    }}>
                                                        {hoveredSubAssembly ? (
                                                            <>
                                                                <div style={{ fontSize: '12px', fontWeight: 500 }}>
                                                                    {hoveredSubAssembly.built}/{hoveredSubAssembly.needed}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div style={{ fontSize: '12px', fontWeight: 500 }}>{overallPct}%</div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', padding: '6px 12px 8px', width: '100%' }}>
                                                    <p className="batch-donut-subtitle">Sub-assemblies</p>

                                                    {labels.map((name, i) => {
                                                        const needed = (sub.bom_per_detector[name] || 0) * detectorTarget;
                                                        const built = sub.built_breakdown[name] || 0;
                                                        const pct = needed > 0 ? Math.min(100, Math.round((built / needed) * 100)) : 0;
                                                        return (
                                                            <div key={name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                                                                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0, display: 'inline-block' }} />
                                                                    <span title={name} style={{ color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>{name.length > 15 ? name.slice(0, 15) + '…' : name}</span>
                                                                </div>
                                                                <span style={{ fontWeight: 500, color: 'var(--color-text-primary)', marginLeft: '6px', flexShrink: 0 }}>{pct}%</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Detectors Donut */}
                                <div className="batch-donut">
                                    {selectedBatchProgress && (() => {
                                        const { built, target, remaining } = selectedBatchProgress.detectors;
                                        const pct = target > 0 ? Math.round((built / target) * 100) : 0;

                                        const chartOptions = {
                                            chart: {
                                                type: 'donut' as const,
                                                background: 'transparent',
                                                sparkline: { enabled: true },
                                                events: {
                                                    dataPointMouseEnter: () => setIsDetectorHovered(true),
                                                    dataPointMouseLeave: () => setIsDetectorHovered(false),
                                                }
                                            },
                                            labels: ['Built', 'Remaining'],
                                            colors: ['#1D9E75', '#9e9d9d'],
                                            dataLabels: { enabled: false },
                                            legend: { show: false },
                                            stroke: { width: 0 },
                                            plotOptions: {
                                                pie: { donut: { size: '85%' } }
                                            },
                                            tooltip: { enabled: false },
                                        };

                                        return (
                                            <div style={{ width: '100%', display: "flex", alignItems: 'center' }}>
                                                <div style={{ position: 'relative', flexShrink: 0, width: '45%', paddingRight: '0.5rem' }}>
                                                    <Chart options={chartOptions} series={[Math.max(built, 0), remaining]} type="donut" height={180} />
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        textAlign: 'center',
                                                        pointerEvents: 'none',
                                                        lineHeight: '1.3',
                                                    }}>
                                                        {isDetectorHovered ? (
                                                            <>
                                                                <div style={{ fontSize: '12px', fontWeight: 500 }}>{built} / {target}</div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div style={{ fontSize: '12px', fontWeight: 500 }}>{pct}%</div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', padding: '6px 12px 8px', width: '50%' }}>
                                                    <p className="batch-donut-subtitle">Detectors</p>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
                                                            <span style={{ color: 'var(--color-text-secondary)', cursor: 'default' }}>Assembled</span>
                                                        </div>
                                                        <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{built}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#c3c0c0', display: 'inline-block' }} />
                                                            <span style={{ color: 'var(--color-text-secondary)', cursor: 'default' }}>Remaining</span>
                                                        </div>
                                                        <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{remaining}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                            </div>
                        </div>

                        {/* Batch Completion */}
                        <div className="toa-chart toa-chart-half">
                            {/* <h1 className="marketing-donut-title">Batch Completion</h1> */}

                            <div className='batch-completion-and-stat'>

                                {selectedBatchProgress && (() => {

                                    const status = selectedBatchProgress.status.toLowerCase();

                                    const lineFill =
                                        status === "completed" ? "100%" :
                                            status === "in_progress" ? "50%" :
                                                "0%";

                                    const stages = [
                                        {
                                            key: "planning",
                                            label: "Planning",
                                            days: selectedBatchProgress.time.planning_days
                                        },
                                        {
                                            key: "in_progress",
                                            label: "In Progress",
                                            days: selectedBatchProgress.time.in_progress_days
                                        },
                                        {
                                            key: "completed",
                                            label: "Complete",
                                            days: null
                                        }
                                    ];

                                    const getStageState = (stageKey: string) => {
                                        if (status === "completed") return "done";
                                        if (status === stageKey) return "active";

                                        if (stageKey === "planning") return "done";
                                        if (stageKey === "in_progress" && status === "completed") return "done";

                                        return "pending";
                                    };

                                    const formatDays = (days: number | null) => {
                                        if (!days) return "-";
                                        return `${Math.max(1, Number(days.toFixed(2)))} Day${Number(days.toFixed(2)) !== 1 ? "s" : ""}`;
                                    };

                                    return (
                                        <div className="batch-completion-wrapper">

                                            {/* Line */}
                                            <div
                                                className="batch-completion-line"
                                                style={{ '--line-fill': lineFill } as React.CSSProperties}
                                            />

                                            {/* Steps */}
                                            {stages.map((stage) => {
                                                const state = getStageState(stage.key);

                                                return (
                                                    <div key={stage.key} className="batch-step">
                                                        <div className={`batch-step-circle ${state}`}>
                                                            {state === "done" && (
                                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                    <path d="M3 8l3.5 3.5L13 4.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        <div className="batch-step-label">{stage.label}</div>
                                                        <div className="batch-step-time">{formatDays(stage.days)}</div>
                                                    </div>
                                                );
                                            })}

                                        </div>
                                    );
                                })()}

                                <div className='batch-stats'>
                                    <div className='batch-stat-card planning'>
                                        <span className="manu-stat-label">Avg. Planning</span>
                                        <span className="manu-stat-value">{formatAvgDays(avgStats.planning)}</span>
                                        <span className="stat-meta">Weighted by batch size</span>
                                    </div>

                                    <div className='batch-stat-card in-progress'>
                                        <span className="manu-stat-label">Avg. Progress</span>
                                        <span className="manu-stat-value">{formatAvgDays(avgStats.inProgress)}</span>
                                        <span className="stat-meta">Weighted by batch size</span>
                                    </div>

                                    <div className='batch-stat-card completed'>
                                        <span className="manu-stat-label">Avg. Completion</span>
                                        <span className="manu-stat-value">{formatAvgDays(avgStats.completion)}</span>
                                        <span className="stat-meta">Weighted by batch size</span>
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>

                </div>

            </div>

            {/* Batch Popup */}
            {showBatchPopup && (
                <div className="active-products-popup-overlay">
                    <div className="active-products-popup-content">

                        <h2 className="edit-org-popup-header">
                            {editingBatch ? 'Edit Batch' : 'Create Batch'}
                        </h2>

                        <p>
                            {editingBatch
                                ? "Modify an existing batch."
                                : "Create a new production batch."}
                        </p>

                        <div className="edit-org-form edit-manage edit-batch-request-form edit-sub-request-form">

                            <label>
                                <strong>Batch Name:</strong>
                                <input
                                    value={batchName}
                                    onChange={(e) => setBatchName(e.target.value)}
                                />
                            </label>

                            <label>
                                <strong>Region:</strong>
                                <input
                                    value={batchRegion}
                                    onChange={(e) => setBatchRegion(e.target.value)}
                                />
                            </label>

                            <label>
                                <strong>Detector Version:</strong>
                                <select
                                    value={batchDetectorVersion}
                                    onChange={(e) => setBatchDetectorVersion(Number(e.target.value))}
                                    disabled={["in_progress", "completed"].includes(editingBatch?.status || "")}
                                    style={
                                        ["in_progress", "completed"].includes(editingBatch?.status || "")
                                            ? { backgroundColor: "#f0f0f0", cursor: "not-allowed" }
                                            : {}
                                    }
                                >
                                    <option value="">Select Detector</option>
                                    {detectors.map(d => (
                                        <option key={d.id} value={d.id}>
                                            {d.name} v{d.version}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <strong>Detectors Needed (Target Quantity):</strong>
                                <input
                                    type="number"
                                    value={batchTargetQty}
                                    onChange={(e) => setBatchTargetQty(Number(e.target.value))}
                                    disabled={editingBatch?.status === "completed"}
                                    style={
                                        editingBatch?.status === "completed"
                                            ? { backgroundColor: "#f0f0f0", cursor: "not-allowed" }
                                            : {}
                                    }
                                />
                            </label>

                            <label className="full-width-section">
                                <strong>Shipment Due:</strong>
                                <input
                                    type="date"
                                    value={batchShipmentDue}
                                    onChange={(e) => setBatchShipmentDue(e.target.value)}
                                />
                            </label>

                            <label className="full-width-section">
                                <strong>Notes:</strong>
                                <textarea
                                    value={batchNotes}
                                    onChange={(e) => setBatchNotes(e.target.value)}
                                />
                            </label>

                        </div>

                        <div className="hh-duo hh-actions edit-org-popup-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => setShowBatchPopup(false)}
                            >
                                Cancel
                            </button>

                            {editingBatch && !["in_progress", "completed"].includes(editingBatch.status) && (
                                <button
                                    className="hh-delete-btn"
                                    onClick={() => {
                                        setBatchToDelete(editingBatch);
                                        setShowConfirmDeleteBatchPopup(true);
                                    }}
                                >
                                    Delete
                                </button>
                            )}

                            <button className="confirm-btn" onClick={saveBatch}>
                                {editingBatch ? 'Save' : 'Create'}
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Confirm Delete Batch Popup */}
            {showConfirmDeleteBatchPopup && batchToDelete && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content hh-del-pop">
                        <div className="hh-delete-inner-pop batch-del-ext" style={{ gap: "2rem" }}>

                            <div className="hh-delete-text-and-icon">

                                <div className="batch-delete-icon-wrapper">
                                    <FaTrashAlt className="batch-delete-icon" />
                                </div>

                                <div>
                                    <h3>Delete Batch</h3>

                                    <p>
                                        Are you sure you want to delete this batch?
                                    </p>

                                    <p style={{ marginTop: "1rem" }}>
                                        <strong>Name:</strong> {batchToDelete.batch_name}<br />
                                        <strong>Region:</strong> {batchToDelete.region}<br />
                                        <strong>Target Detectors:</strong> {batchToDelete.target_quantity}
                                    </p>

                                    <p style={{ marginTop: "1rem", color: "#d92d20" }}>
                                        This action cannot be undone.
                                    </p>
                                </div>

                            </div>

                            <div className="hh-duo hh-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowConfirmDeleteBatchPopup(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="hh-delete-btn"
                                    onClick={async () => {
                                        setShowConfirmDeleteBatchPopup(false);
                                        await handleDeleteBatch();
                                    }}
                                >
                                    Delete Batch
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {/* Planned Locations Popup */}
            {showPlannedLocationsPopup && (
                <div className="active-products-popup-overlay">
                    <div className="planning-loc-content">

                        <div className="manu-popup-title-and-close">
                            <h2>Planned Locations</h2>
                            <button
                                className="new-close-btn"
                                onClick={() => {
                                    setShowPlannedLocationsPopup(false);
                                    setPlannedLocSearch('');
                                }}
                            >
                                x
                            </button>
                        </div>

                        {/* Search */}
                        <div className="hardware3-action-buttons">

                            <div className='manu-planned-location-action-buttons'>
                                <input
                                    type="text"
                                    placeholder="Search locations..."
                                    className="hardware3-open-popup-button"
                                    value={plannedLocSearch}
                                    onChange={(e) => setPlannedLocSearch(e.target.value)}
                                />

                                <FilterDropdown
                                    dropdownClassName="detector-status-dropdown"
                                    sideClass="detector-side-drop"
                                    initialSelected={{
                                        region: plannedRegions,
                                        status: ['Planned', 'Deployed'],
                                    }}
                                    sections={[
                                        {
                                            key: 'region',
                                            title: 'Region',
                                            options: plannedRegions,
                                            type: 'checkbox',
                                        },
                                        {
                                            key: 'status',
                                            title: 'Status',
                                            options: ['Planned', 'Deployed'],
                                            type: 'checkbox',
                                        },
                                    ]}
                                    onChange={(filters) => {
                                        setPlannedLocationFilters({
                                            region: filters.region ?? [],
                                            status: filters.status ?? [],
                                        });
                                    }}
                                />

                                <button className='inv-order-btn' onClick={openCreateLocation}>
                                    <MdAddLocationAlt className='inv-order-btn-bag-svg' />
                                    <span> Location</span>
                                </button>

                            </div>

                            <button className='inv-order-btn inv-order-btn order-btn2' onClick={exportSelectedLocations}>
                                <FaFileCsv className='inv-order-btn-bag-svg' />
                                <span> Export</span>
                            </button>

                        </div>

                        {/* Table */}
                        <div className="hardware3-table-wrapper">
                            <table className="hardware3-table manu-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <div className="toa-efficiency-cell">
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        filteredPlannedLocationsForTable.length > 0 &&
                                                        filteredPlannedLocationsForTable.every(loc =>
                                                            selectedLocations.has(loc.loc_id)
                                                        )
                                                    }
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedLocations(
                                                                new Set(filteredPlannedLocationsForTable.map(loc => loc.loc_id))
                                                            );
                                                        } else {
                                                            setSelectedLocations(new Set());
                                                        }
                                                    }}
                                                />
                                                <span>Location</span>
                                            </div>
                                        </th>
                                        <th>Address</th>
                                        <th>Region</th>
                                        <th>Planned Date</th>
                                        <th>Contact</th>
                                        <th>Authorized</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(filteredPlannedLocationsForTable ?? [])
                                        .filter(loc => {
                                            const q = plannedLocSearch.toLowerCase();

                                            const matchesSearch =
                                                loc.location_name?.toLowerCase().includes(q) ||
                                                loc.address?.toLowerCase().includes(q) ||
                                                loc.contact_person?.toLowerCase().includes(q) ||
                                                loc.contact_email?.toLowerCase().includes(q);

                                            const matchesRegion =
                                                plannedLocationFilters.region.length === 0 ||
                                                plannedLocationFilters.region.includes(loc.region || '');

                                            const matchesStatus =
                                                plannedLocationFilters.status.length === 0 ||
                                                plannedLocationFilters.status.includes(
                                                    loc.is_deployed ? 'Deployed' : 'Planned'
                                                );

                                            return matchesSearch && matchesRegion && matchesStatus;
                                        })
                                        .map(loc => (
                                            <tr
                                                key={loc.loc_id}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => openEditLocation(loc)}
                                            >
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div className="toa-efficiency-cell">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedLocations.has(loc.loc_id)}
                                                            onChange={() => toggleLocationSelection(loc.loc_id)}
                                                        />
                                                        <span>{loc.location_name}</span>
                                                    </div>
                                                </td>
                                                <td>{loc.address || '-'}</td>
                                                <td>{loc.region}</td>
                                                <td>
                                                    {loc.planned_deployment_date
                                                        ? formatDate(loc.planned_deployment_date, false)
                                                        : '-'}
                                                </td>
                                                <td>
                                                    {loc.contact_person
                                                        ? <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                            <span>{loc.contact_person}</span>
                                                            {loc.contact_email && (
                                                                <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                                                                    {loc.contact_email}
                                                                </span>
                                                            )}
                                                            {loc.contact_number && (
                                                                <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                                                                    {loc.contact_number}
                                                                </span>
                                                            )}
                                                        </div>
                                                        : '-'}
                                                </td>

                                                <td
                                                    style={{
                                                        color: loc.is_authorized ? "#20bf55" : "#d32f2f",
                                                        fontWeight: 600
                                                    }}

                                                > {loc.is_authorized ? "Yes" : "No"}</td>

                                                <td>
                                                    <span className={`det-popup-badge ${loc.is_deployed ? 'deployed' : 'planned'}`}>
                                                        {loc.is_deployed ? 'Deployed' : 'Planned'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}

                                    {(plannedLocations ?? []).length === 0 && (
                                        <tr>
                                            <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                                                No planned locations found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>
            )}

            {/* Add/Edit Planned Locations Popup */}
            {showAddorEditPlannedLocationsPopup && (
                <div className="active-products-popup-overlay">
                    <div className="active-products-popup-content">

                        <h2 className="edit-org-popup-header">
                            {editingLocation ? 'Edit Location' : 'Add Location'}
                        </h2>
                        <p>
                            {editingLocation
                                ? "Modify an existing planned location."
                                : "Add a new planned detector location."}
                        </p>

                        <div className="edit-org-form edit-manage ">

                            <label>
                                <strong>Location Name:</strong>
                                <input
                                    value={locName}
                                    onChange={(e) => setLocName(e.target.value)}
                                />
                            </label>

                            <label>
                                <strong>Latitude:</strong>
                                <input
                                    type="number"
                                    step="any"
                                    value={locLat}
                                    onChange={(e) => setLocLat(parseFloat(e.target.value))}
                                    onBlur={() => {
                                        if (typeof locLat === "number" && typeof locLon === "number") {
                                            reverseGeocode(locLat, locLon);
                                        }
                                    }}
                                />

                                <span
                                    style={{
                                        fontSize: '11px',
                                        color: 'var(--color-text-secondary)',
                                        marginTop: '4px',
                                        display: 'block'
                                    }}
                                >
                                    📍 Location details are auto-generated from latitude and longitude.
                                </span>

                            </label>

                            <label>
                                <strong>Longitude:</strong>
                                <input
                                    type="number"
                                    step="any"
                                    value={locLon}
                                    onChange={(e) => setLocLon(parseFloat(e.target.value))}
                                    onBlur={() => {
                                        if (typeof locLat === "number" && typeof locLon === "number") {
                                            reverseGeocode(locLat, locLon);
                                        }
                                    }}
                                />
                            </label>


                            <label>
                                <strong>Address:</strong>
                                <input
                                    type="text"
                                    value={locAddress}
                                    onChange={(e) => {
                                        setLocAddress(e.target.value);
                                        setIsAddressManuallyEdited(true);
                                    }}
                                    placeholder="Address"
                                />
                            </label>

                            <label>
                                <strong>Region:</strong>
                                <input
                                    value={locRegion}
                                    disabled
                                    style={{ backgroundColor: "#f0f0f0", cursor: "not-allowed" }}
                                />
                            </label>

                            <label>
                                <strong>County:</strong>
                                <input
                                    value={locCounty}
                                    disabled
                                    style={{ backgroundColor: "#f0f0f0", cursor: "not-allowed" }}
                                />
                            </label>

                            <label>
                                <strong>Contact Person:</strong>
                                <input
                                    value={locContactPerson}
                                    onChange={(e) => setLocContactPerson(e.target.value)}
                                />
                            </label>

                            <label>
                                <strong>Contact Email:</strong>
                                <input
                                    type="email"
                                    value={locContactEmail}
                                    onChange={(e) => setLocContactEmail(e.target.value)}
                                />
                            </label>

                            <label>
                                <strong>Contact Number:</strong>
                                <input
                                    value={locContactNumber}
                                    onChange={(e) => setLocContactNumber(e.target.value)}
                                />
                            </label>

                            <label>
                                <strong>Planned Deployment Date:</strong>
                                <input
                                    type="date"
                                    value={locPlannedDate}
                                    onChange={(e) => setLocPlannedDate(e.target.value)}
                                />
                            </label>

                            <label>
                                <strong>Installation Authorized:</strong>
                                <input
                                    type="checkbox"
                                    checked={locIsAuthorized}
                                    onChange={(e) => setLocIsAuthorized(e.target.checked)}
                                />
                            </label>

                            {editingLocation &&
                                <label>
                                    <strong>Google Maps Directions:</strong>
                                    <div
                                        className='google-maps-button-div google-maps-button-manu google-maps-button-manu-in-edit'
                                        onClick={() =>
                                            window.open(
                                                `https://www.google.com/maps/search/?api=1&query=${locLat},${locLon}`,
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                        }
                                    >
                                        <img src={googleMapsLogo} alt='gmaps' />
                                        <p>Google Maps</p>
                                    </div>
                                </label>

                            }

                            <label className="full-width-section">
                                <strong>Notes:</strong>
                                <textarea
                                    rows={4}
                                    value={locNote}
                                    onChange={(e) => setLocNote(e.target.value)}
                                />
                            </label>

                        </div>

                        <div className="hh-duo hh-actions edit-org-popup-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => setShowAddorEditPlannedLocationsPopup(false)}
                            >
                                Cancel
                            </button>

                            {editingLocation && (
                                <button
                                    className="hh-delete-btn"
                                    onClick={() => {
                                        setLocToDelete(editingLocation);
                                        setShowConfirmDeleteLocPopup(true);
                                    }}
                                >
                                    Delete
                                </button>
                            )}

                            <button className="confirm-btn" onClick={saveLocation}>
                                {editingLocation ? 'Save' : 'Add'}
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Confirm Delete Location Popup */}
            {showConfirmDeleteLocPopup && locToDelete && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content hh-del-pop">
                        <div className="hh-delete-inner-pop batch-del-ext" style={{ gap: "2rem" }}>

                            <div className="hh-delete-text-and-icon">
                                <div className="batch-delete-icon-wrapper">
                                    <FaTrashAlt className="batch-delete-icon" />
                                </div>
                                <div>
                                    <h3>Delete Location</h3>
                                    <p>Are you sure you want to delete this planned location?</p>
                                    <p style={{ marginTop: "1rem" }}>
                                        <strong>Name:</strong> {locToDelete.location_name}<br />
                                        {locToDelete.address && <><strong>Address:</strong> {locToDelete.address}<br /></>}
                                    </p>
                                    <p style={{ marginTop: "1rem", color: "#d92d20" }}>
                                        This action cannot be undone.
                                    </p>
                                </div>
                            </div>

                            <div className="hh-duo hh-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowConfirmDeleteLocPopup(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="hh-delete-btn"
                                    onClick={handleDeleteLocation}
                                >
                                    Delete Location
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}



        </div>
    )
}

export default Manufacturing